import { useEffect, useRef, useState } from 'react';
import { sfxHover, sfxClick, sfxWhoosh } from '@/lib/soundFX';

interface WorkplaceEnv {
  id: string;
  code: string;
  title: string;
  subtitle: string;
  description: string;
  stats: { label: string; value: string }[];
  accentColor: string;
  secondaryColor: string;
  gridPattern: string;
  departments: string[];
  ctaLabel: string;
  ctaSection: string;
}

const ENVIRONMENTS: WorkplaceEnv[] = [
  {
    id: 'command-floor',
    code: 'ENV-01 // ALPHA SECTOR',
    title: 'THE AI COMMAND FLOOR',
    subtitle: 'Where Intelligence Meets Execution',
    description: 'Step into the nerve center of your AI workforce. 232 specialized agents operate in perfect synchrony — processing data streams, executing campaigns, and delivering results around the clock. No breaks. No burnout. No limits.',
    stats: [
      { label: 'AGENTS ONLINE', value: '232' },
      { label: 'TASKS / HOUR', value: '∞' },
      { label: 'UPTIME', value: '99.9%' },
      { label: 'COST VS HUMAN', value: '−94%' },
    ],
    accentColor: '#00A6EB',
    secondaryColor: '#0066FF',
    gridPattern: 'cyber-grid-cyan',
    departments: ['OPERATIONS', 'ADMIN', 'SALES', 'FINANCE', 'FOUNDER OPS'],
    ctaLabel: 'DEPLOY YOUR TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'marketing-hive',
    code: 'ENV-02 // BETA SECTOR',
    title: 'MARKETING INTELLIGENCE HIVE',
    subtitle: 'Content. Campaigns. Conversions. Automated.',
    description: 'Your marketing department never sleeps. AI copywriters, brand designers, SEO architects, and social media strategists work in parallel — producing content at machine speed with human-level creativity and strategic precision.',
    stats: [
      { label: 'CONTENT PIECES / DAY', value: '500+' },
      { label: 'CAMPAIGNS ACTIVE', value: '24/7' },
      { label: 'ROI MULTIPLIER', value: '12×' },
      { label: 'AGENTS IN SECTOR', value: '31' },
    ],
    accentColor: '#FF0055',
    secondaryColor: '#CC0044',
    gridPattern: 'cyber-grid-magenta',
    departments: ['MARKETING', 'E-COMMERCE', 'CUSTOMER SERVICE'],
    ctaLabel: 'HIRE MARKETING AGENTS',
    ctaSection: 'departments',
  },
  {
    id: 'executive-suite',
    code: 'ENV-03 // GAMMA SECTOR',
    title: 'EXECUTIVE OPERATIONS SUITE',
    subtitle: 'C-Suite Intelligence. Startup Cost.',
    description: 'Access enterprise-grade strategic intelligence without the enterprise price tag. AI CFOs, legal analysts, regulatory researchers, and executive partners deliver boardroom-level insights and decisions at the speed of thought.',
    stats: [
      { label: 'STRATEGIC REPORTS / WEEK', value: '50+' },
      { label: 'COMPLIANCE COVERAGE', value: '100%' },
      { label: 'DECISION LATENCY', value: '<2s' },
      { label: 'SALARY SAVED / YEAR', value: '$480K' },
    ],
    accentColor: '#9B59B6',
    secondaryColor: '#7D3C98',
    gridPattern: 'cyber-grid-purple',
    departments: ['ENTERPRISE', 'FINANCE', 'FOUNDER OPS'],
    ctaLabel: 'ACCESS EXECUTIVE SUITE',
    ctaSection: 'departments',
  },
  {
    id: 'small-business',
    code: 'ENV-04 // DELTA SECTOR',
    title: 'THE FOUNDER\'S COMMAND BRIDGE',
    subtitle: 'Solo Founders. Empire-Level Operations.',
    description: 'Your AI executive team handles every operational layer — legal research, capital sourcing, executive scheduling, and investor relations. You focus on vision. They execute everything else, 24/7.',
    stats: [
      { label: 'TASKS AUTOMATED/DAY', value: '847+' },
      { label: 'FOUNDER HOURS FREED', value: '62 hrs/wk' },
      { label: 'OPERATIONAL COST', value: '−94%' },
      { label: 'AGENTS IN SECTOR', value: '12' },
    ],
    accentColor: '#00FFCC',
    secondaryColor: '#00CC99',
    gridPattern: 'cyber-grid-teal',
    departments: ['FOUNDER OPS', 'SMALL BUSINESS', 'ADMIN'],
    ctaLabel: 'DEPLOY FOUNDER TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'sales',
    code: 'ENV-05 // EPSILON SECTOR',
    title: 'REVENUE GENERATION ENGINE',
    subtitle: 'End-to-End Pipeline. Zero Downtime.',
    description: 'SDR prospecting, enterprise account management, CRM optimization, objection handling, and deal closing — your AI sales force works every lead, every hour, across every channel simultaneously.',
    stats: [
      { label: 'LEADS PROCESSED/DAY', value: '1,200+' },
      { label: 'PIPELINE VELOCITY', value: '+580%' },
      { label: 'CLOSE RATE LIFT', value: '+47%' },
      { label: 'AGENTS IN SECTOR', value: '11' },
    ],
    accentColor: '#FFB800',
    secondaryColor: '#CC9200',
    gridPattern: 'cyber-grid-amber',
    departments: ['SALES', 'CRM', 'OUTBOUND'],
    ctaLabel: 'DEPLOY SALES FORCE',
    ctaSection: 'departments',
  },
  {
    id: 'customer-service',
    code: 'ENV-06 // ZETA SECTOR',
    title: 'CUSTOMER EXPERIENCE MATRIX',
    subtitle: '24/7 Concierge-Grade Support. Infinite Scale.',
    description: 'Live chat coverage, ticket triage, VIP concierge handling, CSAT analytics, and escalation routing — your AI support team delivers 5-star experiences around the clock without burnout or turnover.',
    stats: [
      { label: 'RESPONSE TIME', value: '<2 sec' },
      { label: 'TICKETS RESOLVED/DAY', value: '3,800+' },
      { label: 'CSAT SCORE', value: '98.7%' },
      { label: 'AGENTS IN SECTOR', value: '10' },
    ],
    accentColor: '#00FF88',
    secondaryColor: '#00CC66',
    gridPattern: 'cyber-grid-green',
    departments: ['CUSTOMER SERVICE', 'SUPPORT', 'VIP CONCIERGE'],
    ctaLabel: 'DEPLOY SUPPORT TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'finance',
    code: 'ENV-07 // ETA SECTOR',
    title: 'FINANCIAL INTELLIGENCE VAULT',
    subtitle: 'CFO-Grade Mastery. Startup Cost.',
    description: 'Strategic CFO oversight, real-time bookkeeping, cash flow forecasting, tax compliance, budget optimization, and investor relations — institutional-grade financial intelligence at a fraction of the cost.',
    stats: [
      { label: 'REPORTS GENERATED/MO', value: '180+' },
      { label: 'TAX SAVINGS FOUND', value: 'Avg $47K' },
      { label: 'FORECAST ACCURACY', value: '96.2%' },
      { label: 'AGENTS IN SECTOR', value: '11' },
    ],
    accentColor: '#00A6EB',
    secondaryColor: '#0077CC',
    gridPattern: 'cyber-grid-blue',
    departments: ['FINANCE', 'ACCOUNTING', 'INVESTOR RELATIONS'],
    ctaLabel: 'DEPLOY FINANCE TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'hospitality',
    code: 'ENV-08 // THETA SECTOR',
    title: 'HOSPITALITY OPERATIONS TOWER',
    subtitle: 'STR Portfolio Dominance. Automated.',
    description: 'Dynamic pricing optimization, guest concierge automation, compliance monitoring, multi-channel distribution, and review management — your AI hospitality team maximizes occupancy and 5-star ratings.',
    stats: [
      { label: 'OCCUPANCY RATE', value: '94.8%' },
      { label: 'REVENUE PER UNIT', value: '+67%' },
      { label: 'GUEST RATING', value: '4.97 ★' },
      { label: 'AGENTS IN SECTOR', value: '10' },
    ],
    accentColor: '#FF6B35',
    secondaryColor: '#CC4A1A',
    gridPattern: 'cyber-grid-orange',
    departments: ['HOSPITALITY', 'STR', 'GUEST SERVICES'],
    ctaLabel: 'DEPLOY HOSPITALITY TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'ecommerce',
    code: 'ENV-09 // IOTA SECTOR',
    title: 'E-COMMERCE DOMINANCE HUB',
    subtitle: 'Full-Stack Commerce. Machine Speed.',
    description: 'Product listing optimization, inventory management, PPC campaign automation, fraud prevention, cross-platform sync, and customer lifecycle management — your AI e-commerce team dominates every channel.',
    stats: [
      { label: 'LISTINGS OPTIMIZED', value: '10K+' },
      { label: 'ROAS IMPROVEMENT', value: '+290%' },
      { label: 'FRAUD BLOCKED', value: '99.9%' },
      { label: 'AGENTS IN SECTOR', value: '11' },
    ],
    accentColor: '#A855F7',
    secondaryColor: '#7C3AED',
    gridPattern: 'cyber-grid-purple',
    departments: ['E-COMMERCE', 'AMAZON', 'DIGITAL PRODUCTS'],
    ctaLabel: 'DEPLOY ECOMMERCE TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'operations',
    code: 'ENV-10 // KAPPA SECTOR',
    title: 'OPERATIONAL BACKBONE CORE',
    subtitle: 'The Invisible Infrastructure. Always On.',
    description: 'HR automation, payroll processing, procurement management, compliance monitoring, IT administration, and business intelligence — your operational AI backbone handles the complexity so you never have to.',
    stats: [
      { label: 'PROCESSES AUTOMATED', value: '340+' },
      { label: 'COMPLIANCE ALERTS', value: 'Real-time' },
      { label: 'ADMIN OVERHEAD', value: '−89%' },
      { label: 'AGENTS IN SECTOR', value: '11' },
    ],
    accentColor: '#A855F7',
    secondaryColor: '#7C3AED',
    gridPattern: 'cyber-grid-violet',
    departments: ['OPERATIONS', 'HR', 'PROCUREMENT', 'IT ADMIN'],
    ctaLabel: 'DEPLOY OPS TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'game-dev',
    code: 'ENV-11 // LAMBDA SECTOR',
    title: 'GAME DEV & INTERACTIVE LAB',
    subtitle: 'Unity. Unreal. Godot. Roblox. All Covered.',
    description: 'Game designers, level architects, shader artists, multiplayer engineers, and Roblox creators — your AI game studio ships polished, production-ready experiences at machine speed across every major engine.',
    stats: [
      { label: 'ENGINES COVERED', value: '5+' },
      { label: 'BUILD CYCLES', value: '24/7' },
      { label: 'AGENTS IN SECTOR', value: '20' },
      { label: 'PLATFORMS', value: 'PC/Mobile/XR' },
    ],
    accentColor: '#F59E0B',
    secondaryColor: '#D97706',
    gridPattern: 'cyber-grid-amber',
    departments: ['GAME DESIGN', 'UNITY', 'UNREAL', 'GODOT', 'ROBLOX'],
    ctaLabel: 'DEPLOY GAME TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'spatial-xr',
    code: 'ENV-12 // MU SECTOR',
    title: 'SPATIAL & XR FRONTIER',
    subtitle: 'visionOS. WebXR. Spatial UI. Next Dimension.',
    description: 'XR interface architects, visionOS spatial engineers, immersive web developers, and spatial graphics specialists — your AI XR team builds the next generation of human-computer interaction.',
    stats: [
      { label: 'PLATFORMS', value: 'visionOS/WebXR' },
      { label: 'RENDER LATENCY', value: '<16ms' },
      { label: 'AGENTS IN SECTOR', value: '5' },
      { label: 'IMMERSION LEVEL', value: '∞' },
    ],
    accentColor: '#06B6D4',
    secondaryColor: '#0891B2',
    gridPattern: 'cyber-grid-cyan',
    departments: ['XR INTERFACE', 'VISIONOS', 'SPATIAL GRAPHICS'],
    ctaLabel: 'DEPLOY XR TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'geospatial',
    code: 'ENV-13 // NU SECTOR',
    title: 'GEOSPATIAL INTELLIGENCE HUB',
    subtitle: 'GIS. Mapping. Spatial Data. Earth Intelligence.',
    description: 'GIS analysts, spatial data engineers, GeoAI ML engineers, BIM integration specialists, and drone mapping experts — your AI geospatial team turns location data into actionable intelligence.',
    stats: [
      { label: 'DATA LAYERS', value: '500+' },
      { label: 'MAP ACCURACY', value: '99.7%' },
      { label: 'AGENTS IN SECTOR', value: '13' },
      { label: 'COVERAGE', value: 'Global' },
    ],
    accentColor: '#10B981',
    secondaryColor: '#059669',
    gridPattern: 'cyber-grid-green',
    departments: ['GIS ANALYSIS', 'SPATIAL DATA', 'GEOAI', 'DRONE MAPPING'],
    ctaLabel: 'DEPLOY GIS TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'legal',
    code: 'ENV-14 // XI SECTOR',
    title: 'LEGAL INTELLIGENCE CHAMBER',
    subtitle: 'Client Intake. Document Review. Billing. Automated.',
    description: 'Legal client intake specialists, document review assistants, and billing & time-tracking experts — your AI legal team handles the operational layer of legal practice so attorneys can focus on strategy.',
    stats: [
      { label: 'DOCS REVIEWED/DAY', value: '2,000+' },
      { label: 'INTAKE RESPONSE', value: '<30 sec' },
      { label: 'BILLING ACCURACY', value: '99.9%' },
      { label: 'AGENTS IN SECTOR', value: '3' },
    ],
    accentColor: '#6366F1',
    secondaryColor: '#4F46E5',
    gridPattern: 'cyber-grid-purple',
    departments: ['LEGAL OPS', 'DOCUMENT REVIEW', 'LEGAL BILLING'],
    ctaLabel: 'DEPLOY LEGAL TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'healthcare',
    code: 'ENV-15 // OMICRON SECTOR',
    title: 'HEALTHCARE OPERATIONS UNIT',
    subtitle: 'Medical Billing. Patient Support. Precision.',
    description: 'Medical billing & coding specialists and patient support coordinators — your AI healthcare team reduces claim denials, accelerates reimbursements, and delivers compassionate patient communication at scale.',
    stats: [
      { label: 'CLAIM ACCURACY', value: '99.8%' },
      { label: 'DENIAL REDUCTION', value: '−72%' },
      { label: 'RESPONSE TIME', value: '<5 min' },
      { label: 'AGENTS IN SECTOR', value: '2' },
    ],
    accentColor: '#EF4444',
    secondaryColor: '#DC2626',
    gridPattern: 'cyber-grid-red',
    departments: ['MEDICAL BILLING', 'PATIENT SUPPORT'],
    ctaLabel: 'DEPLOY HEALTHCARE TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'lending-realestate',
    code: 'ENV-16 // PI SECTOR',
    title: 'LENDING & REAL ESTATE COMMAND',
    subtitle: 'Loans. Deals. Properties. Closed Faster.',
    description: 'Loan officer assistants and residential real estate agents — your AI lending and real estate team accelerates underwriting, qualifies buyers, and closes deals with precision and speed.',
    stats: [
      { label: 'LOAN PROCESSING', value: '−60% time' },
      { label: 'DEAL CLOSE RATE', value: '+38%' },
      { label: 'RESPONSE TIME', value: '<2 min' },
      { label: 'AGENTS IN SECTOR', value: '2' },
    ],
    accentColor: '#10B981',
    secondaryColor: '#059669',
    gridPattern: 'cyber-grid-teal',
    departments: ['LOAN ORIGINATION', 'REAL ESTATE', 'DEAL CLOSING'],
    ctaLabel: 'DEPLOY LENDING TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'worldbuilding',
    code: 'ENV-17 // RHO SECTOR',
    title: 'WORLDBUILDING CREATIVE FORGE',
    subtitle: 'Narrative. Lore. Characters. Worlds Built.',
    description: 'Cultural anthropologists, world geographers, historical consultants, narrative structure analysts, and character psychology consultants — your AI worldbuilding team constructs rich, internally consistent fictional universes.',
    stats: [
      { label: 'LORE DEPTH', value: 'Infinite' },
      { label: 'CONSISTENCY SCORE', value: '99.4%' },
      { label: 'AGENTS IN SECTOR', value: '5' },
      { label: 'GENRES COVERED', value: 'All' },
    ],
    accentColor: '#8B5CF6',
    secondaryColor: '#7C3AED',
    gridPattern: 'cyber-grid-violet',
    departments: ['NARRATIVE DESIGN', 'LORE SYSTEMS', 'CHARACTER PSYCH'],
    ctaLabel: 'DEPLOY WORLDBUILDING TEAM',
    ctaSection: 'departments',
  },
  {
    id: 'product-management',
    code: 'ENV-18 // SIGMA SECTOR',
    title: 'PRODUCT INTELLIGENCE TOWER',
    subtitle: 'Roadmaps. Sprints. Insights. Shipped.',
    description: 'Product managers, sprint prioritizers, feedback synthesizers, behavioral design specialists, and experiment trackers — your AI product team turns user insights into shipped features at unprecedented velocity.',
    stats: [
      { label: 'FEATURES SHIPPED', value: '3× faster' },
      { label: 'SPRINT EFFICIENCY', value: '+85%' },
      { label: 'USER FEEDBACK LOOPS', value: 'Automated' },
      { label: 'AGENTS IN SECTOR', value: '5' },
    ],
    accentColor: '#00A6EB',
    secondaryColor: '#0077CC',
    gridPattern: 'cyber-grid-blue',
    departments: ['PRODUCT STRATEGY', 'SPRINT OPS', 'UX RESEARCH'],
    ctaLabel: 'DEPLOY PRODUCT TEAM',
    ctaSection: 'departments',
  },
];

function useIntersection(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function StatCard({ label, value, accent, delay }: { label: string; value: string; accent: string; delay: number }) {
  const { ref, visible } = useIntersection(0.1);
  return (
    <div ref={ref} style={{
      background: 'rgba(10,14,26,0.85)',
      border: `1px solid ${accent}44`,
      padding: '1rem',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      transition: `all 0.6s cubic-bezier(0.23,1,0.32,1) ${delay}ms`,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.95)',
    }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
      <div style={{ fontFamily: 'Orbitron, monospace', fontSize: '1.6rem', fontWeight: 900, color: accent, textShadow: `0 0 20px ${accent}88`, letterSpacing: '0.05em' }}>{value}</div>
      <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.5rem', color: 'rgba(232,244,255,0.45)', letterSpacing: '0.2em', marginTop: '0.25rem' }}>{label}</div>
    </div>
  );
}

function EnvironmentSection({ env, index, onScrollToSection }: { env: WorkplaceEnv; index: number; onScrollToSection: (s: string) => void }) {
  const { ref, visible } = useIntersection(0.1);
  const isEven = index % 2 === 0;

  return (
    <section
      ref={ref}
      style={{
        position: 'relative',
        padding: '6rem 0',
        overflow: 'hidden',
        background: index === 0
          ? 'linear-gradient(180deg, #060810 0%, #080d1a 50%, #060810 100%)'
          : index === 1
          ? 'linear-gradient(180deg, #060810 0%, #0d0810 50%, #060810 100%)'
          : 'linear-gradient(180deg, #060810 0%, #0a0812 50%, #060810 100%)',
      }}
    >
      {/* Animated grid background */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(${env.accentColor}08 1px, transparent 1px),
          linear-gradient(90deg, ${env.accentColor}08 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
        opacity: visible ? 1 : 0,
        transition: 'opacity 1.5s ease',
      }} />

      {/* Radial glow */}
      <div style={{
        position: 'absolute',
        [isEven ? 'left' : 'right']: '-10%',
        top: '20%',
        width: '50vw', height: '60vh',
        background: `radial-gradient(ellipse, ${env.accentColor}10 0%, transparent 70%)`,
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: 'opacity 2s ease',
      }} />

      {/* Sector code top label */}
      <div style={{
        position: 'absolute', top: '2rem',
        [isEven ? 'left' : 'right']: '2rem',
        fontFamily: 'Share Tech Mono, monospace',
        fontSize: '0.55rem',
        color: `${env.accentColor}66`,
        letterSpacing: '0.2em',
        opacity: visible ? 1 : 0,
        transition: 'opacity 1s ease 0.3s',
      }}>
        {env.code}
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 2rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isEven ? '1fr 1fr' : '1fr 1fr',
          gap: '4rem',
          alignItems: 'center',
        }} className="grid-cols-1 md:grid-cols-2">

          {/* Text panel */}
          <div style={{
            order: isEven ? 1 : 2,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : `translateX(${isEven ? '-60px' : '60px'})`,
            transition: 'all 0.9s cubic-bezier(0.23,1,0.32,1) 0.1s',
          }}>
            {/* Title */}
            <div style={{
              fontFamily: 'Orbitron, monospace',
              fontSize: 'clamp(1.4rem, 3vw, 2.2rem)',
              fontWeight: 900,
              color: '#E8F4FF',
              letterSpacing: '0.06em',
              lineHeight: 1.1,
              marginBottom: '0.5rem',
              textShadow: `0 0 40px ${env.accentColor}44`,
            }}>
              {env.title.split(' ').map((word, i) => (
                <span key={i} style={{ color: i === 1 || i === 2 ? env.accentColor : '#E8F4FF' }}>{word} </span>
              ))}
            </div>

            {/* Subtitle */}
            <div style={{
              fontFamily: 'Share Tech Mono, monospace',
              fontSize: '0.75rem',
              color: `${env.accentColor}cc`,
              letterSpacing: '0.15em',
              marginBottom: '1.5rem',
            }}>
              ◈ {env.subtitle}
            </div>

            {/* Divider */}
            <div style={{ width: 80, height: 2, background: `linear-gradient(90deg, ${env.accentColor}, transparent)`, marginBottom: '1.5rem' }} />

            {/* Description */}
            <p style={{
              fontFamily: 'Rajdhani, Share Tech Mono, monospace',
              fontSize: '1rem',
              color: 'rgba(232,244,255,0.7)',
              lineHeight: 1.7,
              marginBottom: '2rem',
              letterSpacing: '0.02em',
            }}>
              {env.description}
            </p>

            {/* Department tags */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2rem' }}>
              {env.departments.map(dept => (
                <span key={dept} style={{
                  fontFamily: 'Orbitron, monospace',
                  fontSize: '0.45rem',
                  letterSpacing: '0.15em',
                  color: env.accentColor,
                  border: `1px solid ${env.accentColor}44`,
                  padding: '0.25rem 0.6rem',
                  background: `${env.accentColor}0a`,
                }}>
                  {dept}
                </span>
              ))}
            </div>

            {/* CTA button */}
            <button
              onClick={() => { sfxClick(); sfxWhoosh(); onScrollToSection(env.ctaSection); }}
              style={{
                fontFamily: 'Orbitron, monospace',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.2em',
                color: '#000',
                background: `linear-gradient(135deg, ${env.accentColor} 0%, ${env.secondaryColor} 100%)`,
                border: 'none',
                padding: '0.8rem 2rem',
                cursor: 'pointer',
                clipPath: 'polygon(8px 0%, 100% 0%, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0% 100%, 0% 8px)',
                boxShadow: `0 0 30px ${env.accentColor}44, 0 0 60px ${env.accentColor}22`,
                transition: 'all 0.2s cubic-bezier(0.23,1,0.32,1)',
                transform: 'scale(1)',
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => { sfxHover(); e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = `0 0 50px ${env.accentColor}66, 0 0 100px ${env.accentColor}33`; }}
              onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = `0 0 30px ${env.accentColor}44, 0 0 60px ${env.accentColor}22`; }}
            >
              ⬡ {env.ctaLabel}
            </button>
          </div>

          {/* Visual panel — holographic stats display */}
          <div style={{
            order: isEven ? 2 : 1,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0) rotateY(0deg)' : `translateX(${isEven ? '60px' : '-60px'}) rotateY(${isEven ? '8deg' : '-8deg'})`,
            transition: 'all 1s cubic-bezier(0.23,1,0.32,1) 0.2s',
          }}>
            {/* Holographic frame */}
            <div style={{
              background: 'rgba(8,12,24,0.9)',
              border: `1px solid ${env.accentColor}44`,
              padding: '2rem',
              position: 'relative',
              backdropFilter: 'blur(20px)',
              boxShadow: `0 0 60px ${env.accentColor}15, inset 0 0 40px rgba(0,0,0,0.5)`,
            }}>
              {/* Corner brackets */}
              {[['top','left'],['top','right'],['bottom','left'],['bottom','right']].map(([v, h]) => (
                <div key={`${v}-${h}`} style={{
                  position: 'absolute', [v]: 8, [h]: 8,
                  width: 16, height: 16,
                  borderTop: v === 'top' ? `2px solid ${env.accentColor}` : 'none',
                  borderBottom: v === 'bottom' ? `2px solid ${env.accentColor}` : 'none',
                  borderLeft: h === 'left' ? `2px solid ${env.accentColor}` : 'none',
                  borderRight: h === 'right' ? `2px solid ${env.accentColor}` : 'none',
                }} />
              ))}

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: `1px solid ${env.accentColor}22`, paddingBottom: '0.75rem' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: env.accentColor, boxShadow: `0 0 10px ${env.accentColor}` }} />
                <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.55rem', color: `${env.accentColor}cc`, letterSpacing: '0.2em' }}>
                  SECTOR METRICS // LIVE
                </span>
                <div style={{ marginLeft: 'auto', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.45rem', color: `${env.accentColor}66` }}>
                  SYS.ONLINE
                </div>
              </div>

              {/* Stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {env.stats.map((stat, i) => (
                  <StatCard key={stat.label} label={stat.label} value={stat.value} accent={env.accentColor} delay={i * 100} />
                ))}
              </div>

              {/* Animated data stream bar */}
              <div style={{ position: 'relative', height: 4, background: 'rgba(255,255,255,0.05)', overflow: 'hidden', borderRadius: 2 }}>
                <div style={{
                  position: 'absolute', top: 0, left: '-100%', width: '60%', height: '100%',
                  background: `linear-gradient(90deg, transparent, ${env.accentColor}, ${env.accentColor}88, transparent)`,
                  animation: 'dataStream 2s linear infinite',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.4rem', color: `${env.accentColor}55`, letterSpacing: '0.15em' }}>DATA STREAM ACTIVE</span>
                <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.4rem', color: `${env.accentColor}55`, letterSpacing: '0.15em' }}>REALAIEMPLOYEES.COM</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom separator */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 1,
        background: `linear-gradient(90deg, transparent 0%, ${env.accentColor}33 30%, ${env.accentColor}55 50%, ${env.accentColor}33 70%, transparent 100%)`,
      }} />
    </section>
  );
}

export function CinematicWorkplaces({ onScrollToSection }: { onScrollToSection: (s: string) => void }) {
  const { ref, visible } = useIntersection(0.05);

  return (
    <div ref={ref}>
      {/* Section intro */}
      <div style={{
        textAlign: 'center',
        padding: '5rem 2rem 3rem',
        background: 'linear-gradient(180deg, #060810 0%, #080d1a 100%)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.8s cubic-bezier(0.23,1,0.32,1)',
      }}>
        <div style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.6rem', color: 'rgba(0,166,235,0.6)', letterSpacing: '0.3em', marginBottom: '1rem' }}>
          ◈ WORKPLACE ENVIRONMENTS ◈
        </div>
        <h2 style={{
          fontFamily: 'Orbitron, monospace',
          fontSize: 'clamp(1.8rem, 4vw, 3rem)',
          fontWeight: 900,
          color: '#E8F4FF',
          letterSpacing: '0.08em',
          marginBottom: '1rem',
          textShadow: '0 0 40px rgba(0,166,235,0.3)',
        }}>
          YOUR AI <span style={{ color: '#00A6EB' }}>WORKFORCE</span> IN ACTION
        </h2>
        <p style={{
          fontFamily: 'Rajdhani, Share Tech Mono, monospace',
          fontSize: '1.1rem',
          color: 'rgba(232,244,255,0.55)',
          maxWidth: 600,
          margin: '0 auto',
          lineHeight: 1.6,
          letterSpacing: '0.03em',
        }}>
          Three specialized environments. Hundreds of AI agents. One unified workforce operating at the speed of light.
        </p>
      </div>

      {ENVIRONMENTS.map((env, i) => (
        <EnvironmentSection key={env.id} env={env} index={i} onScrollToSection={onScrollToSection} />
      ))}
    </div>
  );
}
